import axios, { AxiosResponse } from 'axios';
import { Cookies } from 'react-cookie';
import { URL_CERBERUS, URL_FOUNDATION } from '../../config/keys';
import { DoodleConfig } from '../../styles/custom-icons/doodles/doodle';
import { CerberusAuthPolicy, Group, PlanPolicy } from './model';

const cookies = new Cookies();
const token = cookies.get('id_token') || null;

axios.defaults.headers.common.Authorization = `${token}`;

export async function getGroups(): Promise<AxiosResponse<Group[]>> {
	return await axios.get(
		`${URL_CERBERUS}/api/v1/auth/groups?limit=9999&offset=0`
	);
}

export async function getGroupPolicies(
	groupId: number
): Promise<CerberusAuthPolicy[]> {
	return await axios
		.get(`${URL_CERBERUS}/api/v1/auth/groups/${groupId}/policies`)
		.then(res => res.data)
		.catch(e => {
			console.log(e);
			return [];
		});
}

export async function getUserGroup(groupId: number) {
	return await axios.get(
		`${URL_CERBERUS}/api/v1/auth/groups/list_users?group_id=${groupId}`
	);
}

export async function getUsers() {
	return await axios.get(`${URL_FOUNDATION}/api/users`);
}

export async function addUserToGroup(user_id: number, group_id: number) {
	return await axios.post(`${URL_CERBERUS}/api/v1/auth/users/groups`, {
		user_id,
		group_id
	});
}

export async function delUserFromGroup(user_id: number, group_id: number) {
	return await axios.delete(
		`${URL_CERBERUS}/api/v1/auth/users/groups/${group_id}/${user_id}`
	);
}

export async function getMembers(limit: number, offset: number) {
	return await axios.get(
		`${URL_CERBERUS}/api/v1/auth/users?limit=${limit}&offset=${offset}`
	);
}

export async function getPlanPolicies(): Promise<PlanPolicy[]> {
	return await axios
		.get(`${URL_CERBERUS}/api/v1/auth/plans/policies`)
		.then(res => res.data)
		.catch(e => {
			console.log(e);
			return [];
		});
}

export async function createGroup(body: object) {
	return await axios.post(`${URL_CERBERUS}/api/v1/auth/groups`, body);
}

export async function deleteGroup(groupId: number) {
	return await axios.delete(`${URL_CERBERUS}/api/v1/auth/groups/${groupId}`);
}

export async function updateGroup(body: object) {
	return await axios.put(`${URL_CERBERUS}/api/v1/auth/groups`, body);
}

export type UserAdditionalFields = {
	address?: string;
	city?: string;
	postal_code?: string;
	country?: string;
	latitude?: number;
	longitude?: number;
	color?: string;
	use_address_as_default_map_center?: boolean;
	use_address_as_default_optimization_start?: boolean;
	use_address_as_default_optimization_end?: boolean;
	doodle_configuration?: DoodleConfig
};

export type UserDetails = UserAdditionalFields & {
	id: number;
	is_owner: boolean;
	name: string;
	email: string;
	phone?: string;
	status: boolean;
	photo?: string;
	groups: Array<number>;
	teams: Array<number>;
	uuid: string;
};

export async function getUserById(userId: number): Promise<UserDetails | undefined> {
	return await axios
		.get(`${URL_CERBERUS}/api/v1/auth/users/${userId}`)
		.then(res => res.data);
}

type PutUser = {
	name?: string,
	email?: string,
	phone?: string,
	status?: boolean,
	password?: string,
	roleId?: number
}

export type UserPassWordUpdate = 'Unchanged' | 'Random' | { New: string }


type PutUserBdy = {
	user: PutUser,
	password: UserPassWordUpdate,
	groups: Array<number>,
	teams: Array<number>,
	additional_fields?: UserAdditionalFields,
}

export async function putUser(userId: number, body: PutUserBdy): Promise<undefined> {
	return await axios
		.put(`${URL_CERBERUS}/api/v1/auth/users/${userId}`, body)
		.then(res => res.data);
}

type PostUser = {
	is_owner: boolean,
	name: string,
	email: string,
	phone?: string,
	status: boolean,
	password: UserPassWordUpdate,
	groups: Array<number>,
	teams: Array<number>,
	additional_fields?: UserAdditionalFields,
}

/// Clears the user's two-factor enrolment so they can set it up again;
/// cerberus reserves it to the account's owners.
export async function deleteUserTotp(userId: number): Promise<void> {
	return await axios.delete(`${URL_CERBERUS}/api/v1/auth/users/${userId}/totp`).then(() => undefined);
}

export async function postUser(body: PostUser): Promise<number> {
	return await axios
		.post(`${URL_CERBERUS}/api/v1/auth/users`, body)
		.then(res => res.data);
}

export type CompanySubscriptions = {
	invoicing_start_date?: string,
	invoicing_end_date?: string,
	annual_seats?: number,
	monthly_seats?: number,
	free_seats?: number
}

export async function getCompanySubscriptions(): Promise<CompanySubscriptions | undefined> {
	return await axios
		.get(`${URL_FOUNDATION}/api/v2/company/subscriptions`)
		.then(res => res.data);
}
